<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import SocialFundRequestDocumentsDialog from "@/components/shared/SocialFund/SocialFundRequestDocumentsDialog.vue";
import Notification from "@/utils/plugins/NotificationService";
import { useTableSort } from "@/app/composables/useTableSort";
import { useUiStore } from "@/app/stores/uiStore";
import SocialFundApplicationService from "@/api/services/socialFundApplication.service";
import SocialFundApplicationTypeService from "@/api/services/socialFundApplicationType.service";
import SocialFundCallPeriodService from "@/api/services/socialFundCallPeriod.service";
import { useAuthStore } from "@/app/stores/authStore";
import SocialFundCommunicationDialog from "@/components/shared/SocialFund/SocialFundCommunicationDialog.vue";
import { socialFundFeatures, buildSocialFundFilters, agreementLabel, agreementDates, agreementStatusOptions, finalProofOptions, finalProofIndicator, agreementIndicator, collectionItems, apiErrorMessage, exportFilename } from "@/utils/socialFundV2";
import { getSocialFundStatusStyle } from "@/utils/socialFundStatus";

const uiStore = useUiStore();

const authStore = useAuthStore();
const canManage = computed(() => authStore.getUserLogged?.permissions?.includes("fondo_social:manage"));
const canExport = computed(() => authStore.getUserLogged?.permissions?.includes("fondo_social:view_all"));
const selected = ref([]);
const showCommunication = ref(false);
const selectingAll = ref(false);
const exporting = ref(false);
const loadError = ref("");
const appliedParams = ref({});
let requestGeneration = 0;
const canSelect = computed(() => canManage.value && socialFundFeatures.has("communications"));
const allPageSelected = computed(() => requests.value.length > 0 && requests.value.every(item => selected.value.some(recipient => sameId(recipient.id, item.id))));
const hasAppliedFilters = computed(() => Object.keys(appliedParams.value).some(key => !key.startsWith("order[")));
const sameId = (a, b) => String(a) === String(b);
const isSelected = item => selected.value.some(recipient => sameId(recipient.id, item.id));
const toggleSelected = item => { selected.value = isSelected(item) ? selected.value.filter(recipient => !sameId(recipient.id, item.id)) : [...selected.value, item]; };
const selectPage = () => {
  if (allPageSelected.value) selected.value = selected.value.filter(item => !requests.value.some(row => sameId(row.id, item.id)));
  else requests.value.forEach(item => { if (!isSelected(item)) selected.value.push(item); });
};
const clearSelection = () => { selected.value = []; showCommunication.value = false; };
const selectAllResults = async () => {
  if (selectingAll.value || !canSelect.value || loadError.value) return;
  const generation = requestGeneration;
  selectingAll.value = true;
  uiStore.startLoading();
  try {
    const response = await SocialFundApplicationService.getRecipients({ ...appliedParams.value });
    if (generation !== requestGeneration) return;
    const data = response.data;
    const recipients = data?.recipients || collectionItems(data);
    const total = data?.total ?? data?.["hydra:totalItems"];
    if (data?.["hydra:view"]?.["hydra:next"] || (total !== undefined && Number(total) !== recipients.length)) throw new Error("La API no ha devuelto todos los destinatarios del filtro.");
    const unique = new Map(recipients.map(item => {
      const id = item.applicationId ?? item.id;
      if (id === undefined || id === null) throw new Error("La API ha devuelto un destinatario sin expediente.");
      return [String(id), { id, reference: item.reference, applicant: item.applicantName || item.applicant?.name || (typeof item.applicant === "string" ? item.applicant : ""), email: item.contactEmail || item.email }];
    }));
    selected.value = [...unique.values()];
  } catch (err) { Notification.addNotification(await apiErrorMessage(err, err.message || "No se han podido obtener los destinatarios."), "error", 6000); }
  finally { selectingAll.value = false; uiStore.stopLoading(); }
};
const exportExcel = async () => {
  if (!canExport.value || exporting.value || !totalResults.value || loadError.value) return;
  exporting.value = true;
  uiStore.startLoading();
  try {
    const response = await SocialFundApplicationService.exportApplications({ ...appliedParams.value });
    if (response.data?.type?.includes("json")) throw { response };
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = exportFilename(response.headers?.["content-disposition"]);
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) { Notification.addNotification(await apiErrorMessage(err, "No se ha podido exportar el Excel."), "error", 6000); }
  finally { exporting.value = false; uiStore.stopLoading(); }
};


// Activar cada opción cuando su funcionalidad esté implementada.
const enabledFeatures = {
  reportDownload: socialFundFeatures.has("export"),
  requestActions: false,
};

const socialFundFiltersStorageKey = "social-fund-requests-filters";
const socialFundRestoreFiltersStorageKey = "social-fund-requests-restore-filters";

const page = ref(1);
const router = useRouter();
const itemsPerPage = ref(10);
const filters = ref({
  call: "",
  status: "",
  a51Resolution: "",
  aidType: "",
  from: "",
  to: "",
  referenceOrApplicant: "",
  agreementStatus: "",
  finalProof: "",
});

const callOptions = ref([]);
const aidTypeOptions = ref([]);

// Códigos y etiquetas reales de App\Enum\SocialFundApplicationStatus (backend).
const statusOptions = [
  { value: "received", label: "Solicitud recibida" },
  { value: "in_review_a51", label: "En revisión A51" },
  { value: "pending_director_review", label: "Pendiente de validación Dirección" },
  { value: "approved", label: "Aprobada" },
  { value: "rejected", label: "Denegada" },
  { value: "approved_for_payment", label: "Aprobada para pago" },
  { value: "paid", label: "Pagada (pendiente de justificante)" },
  { value: "closed", label: "Cerrada" },
];

// Opinión de A51 (App\Enum\SocialFundA51Resolution) — no vinculante, distinta
// del estado real de la solicitud. "Sin resolver" no es una opción de filtro:
// SearchFilter no filtra por IS NULL con la estrategia "exact".
const a51ResolutionOptions = [
  { value: "approved", label: "Aprobada" },
  { value: "rejected", label: "Denegada" },
  { value: "undecided", label: "En duda" },
];

const requests = ref([]);
const totalResults = ref(0);
const showRequestDocumentsDialog = ref(false);
const selectedRequestDocuments = ref([]);
const selectedRequestReference = ref("");

const requestSortFields = {
  reference: "reference",
  applicant: "applicant.name",
  aidType: "applicationType.name",
  amount: "requestedAmount",
  // "status" y "documents" son valores calculados en el backend (no columnas
  // de BD): no se pueden ordenar por API. Las cabeceras se mantienen
  // desactivadas hasta que la API soporte estas ordenaciones.
  status: "status",
  documents: "documents",
  updatedAt: "updatedAt",
  receivedAt: "createdAt",
};

// Por defecto, la más antigua sin resolver primero (cola de trabajo:
// evita que una solicitud olvidada quede enterrada bajo las que se
// acaban de tocar). No "updatedAt DESC" — eso prioriza lo reciente,
// no lo urgente.
const { getSortIcon, applySortParams, toggleSort } = useTableSort({
  defaultSort: { field: requestSortFields.receivedAt, direction: "ASC" },
});

const statusLabel = (status) =>
  statusOptions.find((option) => option.value === status)?.label || "Sin estado";


const formatCurrency = (amount) =>
  `${new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount || 0)}`;

const formatDateTime = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};
const formatDateParts = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return { date: "—", time: "" };
  return {
    date: date.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }),
    time: date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }),
  };
};

const mapRequest = (item) => ({
  id: item.id,
  reference: item.reference,
  call: item.callPeriod?.year,
  applicant: item.applicant?.name,
  document: item.applicant?.taxId,
  aidType: item.applicationType?.name,
  amount: item.requestedAmount,
  aidTypeCode: item.applicationType?.code,
  requestedTires: item.requestedTires,
  approvedTires: item.approvedTires,
  agreementStatus: item.agreementStatus,
  agreementStatusLabel: item.agreementStatusLabel,
  agreementSignedByApplicantAt: item.agreementSignedByApplicantAt,
  agreementCountersignedAt: item.agreementCountersignedAt,
  email: item.contactEmail,
  status: item.currentStatusCode,
  statusLabel: item.currentStatusLabel,
  documents: item.documentsCount,
  finalProofStatus: item.finalProofStatus ?? null,
  updatedAt: item.updatedAt,
});

const mapRequestDocument = (document) => ({
  id: document.id,
  name: document.documentTypeLabel,
  originalName: document.originalName,
  status: document.validationStatus,
  // FS-S01: contentUrl es ahora un endpoint autenticado
  // (/api/social_fund_applications/{id}/documents/{docId}/download), no una
  // ruta pública estática — se usa tal cual con Api() (blob), no como <a href>.
  downloadUrl: document.contentUrl || null,
});

const getRequests = async () => {
  const params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  };
  Object.assign(params, buildSocialFundFilters(filters.value));
  applySortParams(params);
  const generation = ++requestGeneration;
  loadError.value = "";

  uiStore.startLoading();
  try {
    const res = await SocialFundApplicationService.getApplications(params);
    if (generation !== requestGeneration) return;
    appliedParams.value = Object.fromEntries(Object.entries(params).filter(([key]) => key !== "page" && key !== "itemsPerPage"));
    requests.value = (res?.data["hydra:member"] || []).map(mapRequest);
    totalResults.value = res?.data["hydra:totalItems"] || 0;
  } catch (err) {
    if (generation !== requestGeneration) return;
    requests.value = []; totalResults.value = 0;
    loadError.value = await apiErrorMessage(err, "Error al cargar las solicitudes de Fondo Social.");
    Notification.addNotification(loadError.value, "error", 6000);
  } finally {
    uiStore.stopLoading();
  }
};

const getFilterCatalogs = async () => {
  uiStore.startLoading();
  try {
    const [typesRes, callsRes] = await Promise.all([
      SocialFundApplicationTypeService.getApplicationTypes(),
      SocialFundCallPeriodService.getCallPeriods(),
    ]);
    aidTypeOptions.value = (typesRes?.data["hydra:member"] || []).map((type) => ({
      value: type.code,
      label: type.name,
    }));
    callOptions.value = (callsRes?.data["hydra:member"] || []).map((call) => ({
      value: String(call.id),
      label: call.label || call.year,
    }));
  } catch {
    Notification.addNotification("Error al cargar los catálogos de Fondo Social.", "error", 6000);
  } finally { uiStore.stopLoading(); }
};

const search = () => {
  clearSelection();
  page.value = 1;
  storeFilters();
  getRequests();
};

// Persistencia de filtros al navegar al detalle y volver (botón "Volver a
// solicitudes" de SocialFundRequestDetail.vue) — mismo patrón que
// Employee.vue/EmployeeItem.vue. Solo se restaura una vez: la vista de
// detalle marca socialFundRestoreFiltersStorageKey justo antes de volver;
// cualquier otra forma de llegar a esta vista (menú, recarga, etc.) empieza
// con los filtros vacíos, como hasta ahora.
const storeFilters = () => {
  sessionStorage.setItem(socialFundFiltersStorageKey, JSON.stringify({
    filters: filters.value,
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  }));
};

const applyStoredFilters = () => {
  const storedFilters = sessionStorage.getItem(socialFundFiltersStorageKey);
  if (!storedFilters) {
    return;
  }

  let parsedFilters;
  try { parsedFilters = JSON.parse(storedFilters); } catch { clearStoredFilters(); return; }
  filters.value = { ...filters.value, ...parsedFilters.filters };
  // El filtro booleano antiguo no equivale a los nuevos estados de firma.
  const obsoleteAgreementFilter = Object.hasOwn(filters.value, "hasSignedAgreement");
  delete filters.value.hasSignedAgreement;
  const invalidAgreementStatus = !!filters.value.agreementStatus && !agreementStatusOptions.some(option => option.value === filters.value.agreementStatus);
  if (!socialFundFeatures.has("agreement") || obsoleteAgreementFilter || invalidAgreementStatus) filters.value.agreementStatus = "";
  if (!finalProofOptions.some(option => option.value === filters.value.finalProof)) filters.value.finalProof = "";
  page.value = obsoleteAgreementFilter || invalidAgreementStatus ? 1 : parsedFilters.page || 1;
  itemsPerPage.value = parsedFilters.itemsPerPage || 10;
};

const clearStoredFilters = () => sessionStorage.removeItem(socialFundFiltersStorageKey);

const applySocialFundFiltersState = () => {
  const shouldRestoreFilters = sessionStorage.getItem(socialFundRestoreFiltersStorageKey) === "true";

  if (shouldRestoreFilters) {
    applyStoredFilters();
    sessionStorage.removeItem(socialFundRestoreFiltersStorageKey);
    return;
  }

  clearStoredFilters();
};

const endOfToday = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return today.getTime();
};

const disabledFromDate = (date) => date.getTime() > endOfToday();

const disabledToDate = (date) => {
  if (date.getTime() > endOfToday()) {
    return true;
  }

  if (!filters.value.from) {
    return false;
  }

  return date.getTime() < new Date(`${filters.value.from}T00:00:00`).getTime();
};

const handleFromDateChange = () => {
  if (filters.value.to && filters.value.to < filters.value.from) {
    filters.value.to = "";
  }
  search();
};

const clearFilters = () => {
  filters.value = {
    call: "",
    status: "",
    a51Resolution: "",
    aidType: "",
    from: "",
    to: "",
    referenceOrApplicant: "",
  agreementStatus: "",
  finalProof: "",
  };
  search();
};

const handleSort = (field) => {
  if ([requestSortFields.status, requestSortFields.documents].includes(field)) return;
  toggleSort(field);
  page.value = 1;
  getRequests();
};

const handlePageChange = () => {
  storeFilters();
  getRequests();
};

watch(itemsPerPage, () => {
  page.value = 1;
  storeFilters();
  getRequests();
});

watch(() => canManage.value, clearSelection);

onMounted(() => {
  applySocialFundFiltersState();
  getFilterCatalogs();
  getRequests();
});

const showPendingAction = (message) => Notification.info(`${message} estará disponible próximamente.`, 3500);

const openRequest = (request) => router.push({ name: "social-fund-request-detail", params: { id: request.id } });

const openRequestDocuments = async (request) => {
  uiStore.startLoading();
  try {
    const res = await SocialFundApplicationService.getApplication(request.id);
    if (!res?.data) {
      Notification.addNotification("No se ha podido cargar la documentación de la solicitud.", "error", 6000);
      return;
    }
    selectedRequestReference.value = res.data.reference || request.reference;
    selectedRequestDocuments.value = (res.data.documents || []).map(mapRequestDocument);
    showRequestDocumentsDialog.value = true;
  } finally {
    uiStore.stopLoading();
  }
};
</script>

<template>
  <main class="social-fund-requests">
    <HeaderTitle title="Fondo social" />

    <section class="social-fund-requests__content">
      <div class="social-fund-requests__section-title">
        <svg class="social-fund-requests__search-icon" viewBox="0 0 1024 1024" aria-hidden="true">
          <path
            d="M641.8 737.4c-66.4 47.3-147.7 75.2-235.5 75.2-224.4 0-406.3-181.9-406.3-406.3s181.9-406.3 406.3-406.3c224.4 0 406.3 181.9 406.3 406.3 0 87.8-27.8 169.1-75.2 235.5l266.9 266.9c26.3 26.3 26.1 68.4 0 94.5l-1.1 1.1c-26 26-68.4 26.1-94.5 0l-266.9-266.9zM406.3 717c171.6 0 310.7-139.1 310.7-310.7s-139.1-310.7-310.7-310.7-310.7 139.1-310.7 310.7S234.7 717 406.3 717z" />
        </svg>
        <h2>Solicitudes</h2>
      </div>

      <el-card class="card-search social-fund-requests__filters w-100" body-style="padding:0">
        <div class="social-fund-requests__filters-content">
          <!-- Fila 1: localizar un expediente -->
          <div class="row social-fund-requests__filter-row">
            <div class="col-lg-6 col-md-12 col-12">
              <div class="form-item">
                <label for="request-reference">Referencia, solicitante o contacto</label>
                <el-input id="request-reference" v-model="filters.referenceOrApplicant" class="w-100"
                  placeholder="Buscar por referencia, solicitante, contacto o DNI/NIF/CIF" clearable @change="search"
                  @clear="search" />
              </div>
            </div>
            <div class="col-lg-3 col-md-6 col-12">
              <div class="form-item">
                <label for="request-call">Convocatoria</label>
                <el-select id="request-call" v-model="filters.call" class="w-100" placeholder="Seleccionar convocatoria"
                  clearable filterable @change="search">
                  <el-option v-for="call in callOptions" :key="call.value" :label="call.label" :value="call.value" />
                </el-select>
              </div>
            </div>
            <div class="col-lg-3 col-md-6 col-12">
              <div class="form-item">
                <label for="request-aid-type">Tipo de ayuda</label>
                <el-select id="request-aid-type" v-model="filters.aidType" class="w-100"
                  placeholder="Seleccionar tipo de ayuda" clearable filterable @change="search">
                  <el-option v-for="aidType in aidTypeOptions" :key="aidType.value" :label="aidType.label"
                    :value="aidType.value" />
                </el-select>
              </div>
            </div>
          </div>

          <!-- Fila 2: en qué punto está y qué documentación tiene -->
          <div class="row social-fund-requests__filter-row">
            <div class="col-lg-3 col-md-6 col-12">
              <div class="form-item">
                <label for="request-status">Estado</label>
                <el-select id="request-status" v-model="filters.status" class="w-100" placeholder="Seleccionar estado"
                  clearable filterable @change="search">
                  <template v-if="filters.status" #prefix>
                    <span class="social-fund-requests__status-dot" :style="getSocialFundStatusStyle(filters.status)" />
                  </template>
                  <el-option v-for="status in statusOptions" :key="status.value" :label="status.label"
                    :value="status.value">
                    <span class="social-fund-requests__status-option">
                      <span class="social-fund-requests__status-dot" :style="getSocialFundStatusStyle(status.value)" />
                      {{ status.label }}
                    </span>
                  </el-option>
                </el-select>
              </div>
            </div>
            <div class="col-lg-3 col-md-6 col-12">
              <div class="form-item">
                <label for="request-a51-resolution">Opinión de A51</label>
                <el-select id="request-a51-resolution" v-model="filters.a51Resolution" class="w-100"
                  placeholder="Seleccionar opinión de A51" clearable filterable @change="search">
                  <el-option v-for="option in a51ResolutionOptions" :key="option.value" :label="option.label"
                    :value="option.value" />
                </el-select>
              </div>
            </div>
            <div v-if="socialFundFeatures.has('agreement')" class="col-lg-3 col-md-6 col-12">
              <div class="form-item">
                <label for="signed-agreement">Convenio</label>
                <el-select id="signed-agreement" v-model="filters.agreementStatus" class="w-100" @change="search">
                  <el-option label="Todos" value="" /><el-option v-for="option in agreementStatusOptions" :key="option.value" :label="option.label" :value="option.value" />
                </el-select>
              </div>
            </div>
            <div class="col-lg-3 col-md-6 col-12">
              <div class="form-item">
                <label for="final-proof">Justificante final</label>
                <el-select id="final-proof" v-model="filters.finalProof" class="w-100" @change="search">
                  <el-option label="Todos" value="" /><el-option v-for="option in finalProofOptions" :key="option.value" :label="option.label" :value="option.value" />
                </el-select>
              </div>
            </div>
          </div>

          <!-- Fila 3: fechas y acciones -->
          <div class="row social-fund-requests__filter-row social-fund-requests__filter-row--actions">
            <div class="col-lg-3 col-md-3 col-12">
              <div class="form-item date-picker">
                <label for="request-from">Desde</label>
                <el-date-picker popper-class="social-fund-date-picker" id="request-from" v-model="filters.from" class="w-100" type="date" format="DD/MM/YYYY"
                  value-format="YYYY-MM-DD" :disabled-date="disabledFromDate" @change="handleFromDateChange" />
              </div>
            </div>
            <div class="col-lg-3 col-md-3 col-12">
              <div class="form-item date-picker">
                <label for="request-to">Hasta</label>
                <el-date-picker popper-class="social-fund-date-picker" id="request-to" v-model="filters.to" class="w-100" type="date" format="DD/MM/YYYY"
                  value-format="YYYY-MM-DD" :disabled-date="disabledToDate" @change="search" />
              </div>
            </div>
            <div class="col-lg-6 col-md-6 col-12 social-fund-requests__filter-actions">
              <button type="button" class="btn btn_red btn-search" @click="search">BUSCAR</button>
              <button type="button" class="btn btn_yellow social-fund-requests__clear-button"
                @click="clearFilters">LIMPIAR FILTROS</button>
            </div>
          </div>
        </div>
      </el-card>

      <el-card class="social-fund-requests__list-card w-100" body-style="padding:0">
        <div class="social-fund-requests__list-heading">
          <h3>Solicitudes totales <span>{{ totalResults }}</span></h3>
          <button v-if="enabledFeatures.reportDownload && canExport" type="button" class="btn btn_yellow btn-search social-fund-requests__export" :disabled="exporting || !totalResults || !!loadError" @click="exportExcel">{{ exporting ? 'GENERANDO...' : 'EXPORTAR EXCEL' }}</button>
        </div>

        <div class="social-fund-requests__tabs" role="tablist" aria-label="Alcance de solicitudes">
          <button type="button" class="active">TODAS LAS SOLICITUDES</button>
        </div>

        <p v-if="loadError" role="alert" class="social-fund-requests__empty">{{ loadError }}</p>
        <div v-if="canSelect" class="social-fund-requests__selection">
          <el-checkbox class="d-md-none" :model-value="allPageSelected" :disabled="!requests.length" @change="selectPage">Seleccionar página</el-checkbox>
          <span>{{ selected.length }} seleccionados</span>
          <button v-if="hasAppliedFilters && totalResults" type="button" class="btn btn_yellow" :disabled="selectingAll || !!loadError" @click="selectAllResults">Seleccionar los {{ totalResults }} resultados del filtro</button>
          <button v-if="selected.length" type="button" class="btn btn_red" @click="showCommunication = true">ENVIAR COMUNICADO</button>
          <button v-if="selected.length" type="button" class="btn btn_yellow" @click="clearSelection">LIMPIAR SELECCIÓN</button>
        </div>
        <div class="table table-not-mobile social-fund-requests__table" :class="{ 'social-fund-requests__table--agreement': socialFundFeatures.has('agreement') }">
          <table>
            <thead>
              <tr>
                <th v-if="canSelect" class="social-fund-requests__selection-cell"><el-checkbox :model-value="allPageSelected" aria-label="Seleccionar página" @change="selectPage" /></th>
                <th><button type="button" @click="handleSort(requestSortFields.reference)">Referencia <font-awesome-icon
                      :icon="getSortIcon(requestSortFields.reference)" /></button></th>
                <th><button type="button" @click="handleSort(requestSortFields.applicant)">Solicitante
                    <font-awesome-icon :icon="getSortIcon(requestSortFields.applicant)" /></button></th>
                <th><button type="button" @click="handleSort(requestSortFields.aidType)">Tipo de ayuda
                    <font-awesome-icon :icon="getSortIcon(requestSortFields.aidType)" /></button></th>
                <th><button type="button" @click="handleSort(requestSortFields.amount)">Monto solicitado
                    <font-awesome-icon :icon="getSortIcon(requestSortFields.amount)" /></button></th>
                <th><span>Estado</span></th>
                <th><span>Documentación</span></th>
                <th><button type="button" @click="handleSort(requestSortFields.updatedAt)">Actualizado
                    <font-awesome-icon :icon="getSortIcon(requestSortFields.updatedAt)" /></button></th>
                <th v-if="socialFundFeatures.has('agreement')" class="social-fund-requests__indicator-cell"><span>Convenio</span></th>
                <th class="social-fund-requests__indicator-cell"><span>Justificante</span></th>
                <th v-if="enabledFeatures.requestActions" class="social-fund-requests__actions-cell"><span class="visually-hidden">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="request in requests" :key="request.id" class="pointer" @click="openRequest(request)">
                <td v-if="canSelect" class="social-fund-requests__selection-cell" @click.stop><el-checkbox :model-value="isSelected(request)" :aria-label="'Seleccionar ' + request.reference" @change="toggleSelected(request)" /></td>
                <td class="social-fund-requests__reference">{{ request.reference }}</td>
                <td><strong>{{ request.applicant }}</strong><small>{{ request.document }}</small></td>
                <td>{{ request.aidType }}</td>
                <td><template v-if="socialFundFeatures.has('tires') && request.aidTypeCode === 'solicitud_neumaticos'">{{ request.requestedTires ?? '—' }} neum. (concedidos {{ request.approvedTires ?? '—' }})</template><template v-else>{{ formatCurrency(request.amount) }}</template></td>
                <td><span class="social-fund-requests__status"
                    :style="getSocialFundStatusStyle(request.status)">{{
                      request.statusLabel || statusLabel(request.status) }}</span>
                </td>
                <td><button type="button" class="social-fund-requests__document-link"
                    @click.stop="openRequestDocuments(request)"><font-awesome-icon icon="paperclip" /> {{
                      request.documents }} {{ request.documents === 1 ? 'archivo' : 'archivos'
                    }}</button></td>
                <td class="social-fund-requests__updated-cell">{{ formatDateParts(request.updatedAt).date }}<small>{{ formatDateParts(request.updatedAt).time }}</small></td>
                <td v-if="socialFundFeatures.has('agreement')" class="social-fund-requests__indicator-cell"
                  :title="[agreementIndicator(request).label, agreementDates(request)].filter(Boolean).join('\n')"><font-awesome-icon
                    :icon="agreementIndicator(request).icon" class="social-fund-requests__indicator"
                    :class="'social-fund-requests__indicator--' + agreementIndicator(request).tone"
                    :aria-label="'Convenio: ' + agreementIndicator(request).label" role="img" /></td>
                <td class="social-fund-requests__indicator-cell" :title="finalProofIndicator(request.finalProofStatus).label"><font-awesome-icon
                    :icon="finalProofIndicator(request.finalProofStatus).icon" class="social-fund-requests__indicator"
                    :class="'social-fund-requests__indicator--' + finalProofIndicator(request.finalProofStatus).tone"
                    :aria-label="finalProofIndicator(request.finalProofStatus).label" role="img" /></td>
                <td v-if="enabledFeatures.requestActions" class="social-fund-requests__actions-cell">
                  <div class="social-fund-requests__actions-wrap"><button type="button"
                      class="social-fund-requests__more-action" :aria-label="`Acciones para ${request.reference}`"
                      @click.stop="showPendingAction(`Las acciones de ${request.reference}`)"><font-awesome-icon
                        icon="ellipsis-v" /></button></div>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="!requests.length && !loadError" class="social-fund-requests__empty">No se han encontrado solicitudes con
            los filtros seleccionados.</p>
        </div>

        <div class="table-mobile social-fund-requests__mobile-list">
          <el-card v-for="request in requests" :key="request.id" class="social-fund-requests__mobile-card"
            @click="openRequest(request)">
            <div class="social-fund-requests__mobile-card-head">
              <strong>{{ request.reference }}</strong>
              <el-checkbox v-if="canSelect" :model-value="isSelected(request)" :aria-label="'Seleccionar ' + request.reference" @click.stop @change="toggleSelected(request)" />
              <span class="social-fund-requests__status"
                :style="getSocialFundStatusStyle(request.status)">{{
                  request.statusLabel || statusLabel(request.status) }}</span>
            </div>
            <p><b>Solicitante:</b> <span>{{ request.applicant }}<small>{{ request.document }}</small></span></p>
            <p><b>Tipo de ayuda:</b> <span>{{ request.aidType }}</span></p>
            <p v-if="socialFundFeatures.has('tires') && request.aidTypeCode === 'solicitud_neumaticos'"><b>Neumáticos:</b><span>Solicitados {{ request.requestedTires ?? '—' }} · Concedidos {{ request.approvedTires ?? '—' }}</span></p>
            <p v-else><b>Importe solicitado:</b> <span>{{ formatCurrency(request.amount) }}</span></p>
            <p v-if="socialFundFeatures.has('agreement')"><b>Convenio:</b><span><font-awesome-icon :icon="agreementIndicator(request).icon"
                  class="social-fund-requests__indicator" :class="'social-fund-requests__indicator--' + agreementIndicator(request).tone" />
                {{ agreementLabel(request) }}<small class="social-fund-requests__agreement-dates">{{ agreementDates(request) }}</small></span></p>
            <p><b>Documentación:</b> <button type="button" class="social-fund-requests__document-link"
                @click.stop="openRequestDocuments(request)"><font-awesome-icon icon="paperclip" /> {{ request.documents
                }} {{ request.documents === 1 ? 'archivo' : 'archivos'
                }}</button></p>
            <p><b>Justificante final:</b> <span><font-awesome-icon :icon="finalProofIndicator(request.finalProofStatus).icon"
                  class="social-fund-requests__indicator" :class="'social-fund-requests__indicator--' + finalProofIndicator(request.finalProofStatus).tone" />
                {{ finalProofIndicator(request.finalProofStatus).label }}</span></p>
            <p><b>Actualizado:</b> <span>{{ formatDateTime(request.updatedAt) }}</span></p>
          </el-card>
          <p v-if="!requests.length && !loadError" class="social-fund-requests__empty">No se han encontrado solicitudes con
            los filtros seleccionados.</p>
        </div>

        <div class="social-fund-requests__pagination-wrap">
          <p>Mostrando {{ requests.length ? (page - 1) * itemsPerPage + 1 : 0 }} a {{ Math.min(page *
            itemsPerPage, totalResults) }} de {{ totalResults }} solicitudes</p>
          <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" :total="totalResults"
            :page-size-options="[5, 10, 20, 50]" class="social-fund-requests__pagination" @change="handlePageChange"
            @size-change="page = 1" />
        </div>
      </el-card>
    </section>

    <SocialFundCommunicationDialog v-if="canSelect" v-model="showCommunication" :recipients="selected" @sent="clearSelection" />
    <SocialFundRequestDocumentsDialog v-model="showRequestDocumentsDialog" :reference="selectedRequestReference"
      :documents="selectedRequestDocuments" />
  </main>
</template>

<style lang="scss" scoped>
.social-fund-requests__agreement-dates { white-space: pre-line; }
.social-fund-requests__table td.social-fund-requests__updated-cell { overflow-wrap: normal; white-space: nowrap; }
.social-fund-requests__indicator { font-size: 1.25rem; }
.social-fund-requests__mobile-card .social-fund-requests__indicator { font-size: 1rem; margin-right: 4px; }
.social-fund-requests__indicator--validated { color: #2e7d32; }
.social-fund-requests__indicator--pending { color: #d97706; }
.social-fund-requests__indicator--rejected { color: #c62828; }
.social-fund-requests__indicator--applicant { color: #2563eb; }
.social-fund-requests__indicator--director { color: #7c3aed; }
.social-fund-requests__indicator--missing { color: #b0b7c3; }

.social-fund-requests__selection { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 12px 20px; }
.social-fund-requests__selection .btn { flex: 0 1 auto; width: auto; min-height: 44px; margin: 0 !important; white-space: normal; }
.social-fund-requests__selection .btn_red { color: #fff; }
.social-fund-requests__table .social-fund-requests__selection-cell { width: 38px; padding: 6px !important; }
.social-fund-requests__table th.social-fund-requests__selection-cell :deep(.el-checkbox) { display: flex; height: auto; margin: 0; }
.social-fund-requests__table th button:disabled { cursor: default; }
.social-fund-requests__export { color: #474ce6 !important; margin: 0 !important; min-height: 44px; }
.social-fund-requests__export:hover, .social-fund-requests__export:focus-visible, .social-fund-requests__export:active { background: #e4c20c; color: #474ce6 !important; }
.social-fund-requests__export:disabled { cursor: not-allowed; opacity: .5; }
@media (max-width: 767px) { .social-fund-requests__mobile-card-head { flex-wrap: wrap; } .social-fund-requests__mobile-card-head .social-fund-requests__status { max-width: 100%; white-space: normal; } .social-fund-requests__mobile-card .social-fund-requests__document-link { white-space: normal; } .social-fund-requests__list-heading { flex-wrap: wrap; gap: 12px; } .social-fund-requests__selection .btn { width: 100%; } .social-fund-requests__export { width: 100%; max-width: 100%; } }

.social-fund-requests {
  color: #263052;
}

.social-fund-requests__content {
  padding-bottom: 26px;
}

.social-fund-requests__section-title {
  align-items: center;
  color: $bg-blue;
  display: flex;
  gap: 12px;
  margin: 0 0 14px;
}

.social-fund-requests__search-icon {
  fill: $bg-blue;
  height: 42px;
  width: 42px;
}

.social-fund-requests__section-title h2 {
  font-size: 2.5em;
  font-weight: 900;
  letter-spacing: -.05em;
  margin: 0;
}

.social-fund-requests__filters,
.social-fund-requests__list-card {
  background: #fff !important;
  border-radius: 15px;
}

.social-fund-requests__filters :deep(.el-card__body),
.social-fund-requests__list-card :deep(.el-card__body) {
  padding: 0 !important;
}

.social-fund-requests__filter-row {
  row-gap: 10px;
}

.social-fund-requests__filter-row .form-item {
  margin-bottom: 6px;
}

.social-fund-requests__filter-row label {
  color: #283150;
  font-size: 15px;
  font-weight: 800;
  margin-bottom: 7px;
}

.social-fund-requests__filter-row--actions {
  margin-top: 4px;
}

.social-fund-requests__filter-actions {
  align-items: flex-end;
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  padding-bottom: 6px;
}

.social-fund-requests__filter-actions .btn {
  align-items: center;
  display: inline-flex;
  flex: 0 0 220px;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 44px;
  width: 220px;
}

.social-fund-requests__filter-actions .btn_red {
  color: #fff !important;
}

.social-fund-requests__clear-button {
  color: $bg-blue !important;
}

.social-fund-requests__list-card {
  margin-top: 18px;
  overflow: hidden;
}

.social-fund-requests__list-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 18px 24px 10px;
}

.social-fund-requests__list-heading h3 {
  color: #202845;
  font-size: 18px;
  font-weight: 900;
  margin: 0;
}

.social-fund-requests__list-heading h3 span {
  background: #eff0ff;
  border-radius: 15px;
  color: $bg-blue;
  display: inline-block;
  font-size: 14px;
  margin-left: 8px;
  padding: 5px 12px;
}

.social-fund-requests__download {
  align-items: center;
  background: #fff;
  border: 0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(33, 43, 83, .16);
  color: #202845;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.social-fund-requests__tabs {
  border-bottom: 1px solid #e2e4ee;
  display: flex;
  gap: 16px;
  margin: 0 24px 12px;
}

.social-fund-requests__tabs button {
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  color: #4c55df;
  font-size: 14px;
  font-weight: 800;
  padding: 10px 12px;
}

.social-fund-requests__tabs button.active {
  border-bottom-color: $bg-blue;
}

.social-fund-requests__table {
  margin: 0 18px;
  width: calc(100% - 36px);
}

.social-fund-requests__table table {
  min-width: 0;
  table-layout: fixed;
}

.social-fund-requests__table th {
  padding-bottom: 12px;
  vertical-align: middle;
}

.social-fund-requests__table th button {
  align-items: center;
  display: inline-flex;
  flex-wrap: nowrap;
  background: transparent;
  border: 0;
  color: $bg-blue;
  font-size: 14px;
  font-weight: 800;
  padding: 0;
  line-height: 1.2;
  text-align: left;
  white-space: nowrap;
}

.social-fund-requests__table th button .svg-inline--fa {
  flex-shrink: 0;
  font-size: 11px;
  margin-left: 4px;
}

.social-fund-requests__table td {
  font-size: 14px;
  overflow-wrap: anywhere;
  vertical-align: middle;
}

.social-fund-requests__table th:nth-child(1 of :not(.social-fund-requests__selection-cell)) {
  width: 14%;
}

.social-fund-requests__table th:nth-child(2 of :not(.social-fund-requests__selection-cell)) {
  width: 18%;
}

.social-fund-requests__table th:nth-child(3 of :not(.social-fund-requests__selection-cell)) {
  width: 12%;
}

.social-fund-requests__table th:nth-child(4 of :not(.social-fund-requests__selection-cell)) {
  width: 15%;
}

.social-fund-requests__table th:nth-child(5 of :not(.social-fund-requests__selection-cell)) {
  width: 14%;
}

.social-fund-requests__table th:nth-child(6 of :not(.social-fund-requests__selection-cell)) {
  width: 14%;
}

.social-fund-requests__table th:nth-child(7 of :not(.social-fund-requests__selection-cell)) {
  width: 10%;
}

.social-fund-requests__table th.social-fund-requests__indicator-cell {
  width: 104px;
}

.social-fund-requests__table th.social-fund-requests__actions-cell {
  width: 40px;
}

.social-fund-requests__table .social-fund-requests__indicator-cell {
  text-align: center;
  white-space: nowrap;
}

.social-fund-requests__table td small,
.social-fund-requests__mobile-card small {
  color: #858b9f;
  display: block;
  margin-top: 2px;
}

.social-fund-requests__reference {
  color: #27304f;
  font-weight: 900;
}

.social-fund-requests__status {
  align-items: center;
  background: var(--status-bg, #f7f7f8);
  border: 1px solid var(--status-border, #d3d7e6);
  color: var(--status-text, #757a88);
  border-radius: 15px;
  box-sizing: border-box;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  line-height: 1.2;
  min-height: 30px;
  padding: 6px 10px;
  vertical-align: middle;
  white-space: nowrap;
}

.social-fund-requests__table .social-fund-requests__status {
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
}

.social-fund-requests__status-option {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.social-fund-requests__status-dot {
  background: var(--status-dot, #9ba0ad);
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 8px;
  height: 8px;
  width: 8px;
}

.social-fund-requests__status::before {
  background: var(--status-dot, #9ba0ad);
  border-radius: 50%;
  content: "";
  flex: 0 0 7px;
  height: 7px;
  width: 7px;
}










.social-fund-requests__document-link {
  background: transparent;
  border: 0;
  color: $bg-blue;
  font-weight: 700;
  padding: 0;
  text-decoration: underline;
  white-space: nowrap;
}

.social-fund-requests__document-link .svg-inline--fa {
  color: #202845;
  margin-right: 7px;
  text-decoration: none;
}

.social-fund-requests__more-action {
  align-items: center;
  background: transparent;
  border: 0;
  color: #f9156f;
  display: flex;
  font-size: 20px;
  justify-content: center;
  line-height: 1;
  margin: 0;
  min-height: 32px;
  padding: 0;
  width: 100%;
}

.social-fund-requests__more-action .svg-inline--fa {
  display: block;
  margin: 0;
}

.social-fund-requests__table td.social-fund-requests__actions-cell {
  padding: 0 !important;
  text-align: center;
  vertical-align: middle;
}

.social-fund-requests__actions-wrap {
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
}

.social-fund-requests__empty {
  color: #747d9f;
  margin: 22px 0;
  text-align: center;
}

.table-mobile {
  display: none;
}

.social-fund-requests__pagination-wrap {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 14px 24px 18px;
}

.social-fund-requests__pagination-wrap p {
  color: #747d9f;
  font-size: 14px;
  margin: 0;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (min-width: 992px) and (max-width: 1799px) {
  .social-fund-requests__table {
    margin: 0 12px;
    width: calc(100% - 24px);
  }

  .social-fund-requests__table table {
    border-spacing: 0 16px;
    min-width: 0;
    table-layout: fixed;
  }

  .social-fund-requests__table th,
  .social-fund-requests__table td {
    padding: 9px 8px !important;
  }

  .social-fund-requests__table th,
  .social-fund-requests__table td {

    &:nth-child(6 of :not(.social-fund-requests__selection-cell)) {
      display: none;
    }
  }

  .social-fund-requests__table th:nth-child(1 of :not(.social-fund-requests__selection-cell)) {
    width: 16%;
  }

  .social-fund-requests__table th:nth-child(2 of :not(.social-fund-requests__selection-cell)) {
    width: 26%;
  }

  .social-fund-requests__table th:nth-child(3 of :not(.social-fund-requests__selection-cell)) {
    width: 13%;
  }

  .social-fund-requests__table th:nth-child(4 of :not(.social-fund-requests__selection-cell)) {
    width: 16%;
  }

  .social-fund-requests__table th:nth-child(5 of :not(.social-fund-requests__selection-cell)) {
    width: 20%;
  }

  .social-fund-requests__table th:nth-child(7 of :not(.social-fund-requests__selection-cell)) {
    width: 15%;
  }

  .social-fund-requests__table th.social-fund-requests__indicator-cell {
    width: 96px;
  }

  .social-fund-requests__table td {
    font-size: 13px;
    overflow-wrap: anywhere;
  }
}

@media (min-width: 768px) and (max-width: 991px) {
  .social-fund-requests__table {
    margin: 0 8px;
    width: calc(100% - 16px);
  }

  .social-fund-requests__table table {
    border-spacing: 0 12px;
    min-width: 0;
    table-layout: fixed;
  }

  .social-fund-requests__table th,
  .social-fund-requests__table td {
    padding: 8px 7px !important;

    &:nth-child(3 of :not(.social-fund-requests__selection-cell)),
    &:nth-child(4 of :not(.social-fund-requests__selection-cell)),
    &:nth-child(6 of :not(.social-fund-requests__selection-cell)),
    &:nth-child(7 of :not(.social-fund-requests__selection-cell)) {
      display: none;
    }
  }

  .social-fund-requests__table th:nth-child(1 of :not(.social-fund-requests__selection-cell)) {
    width: 22%;
  }

  .social-fund-requests__table th:nth-child(2 of :not(.social-fund-requests__selection-cell)) {
    width: 36%;
  }

  .social-fund-requests__table th:nth-child(5 of :not(.social-fund-requests__selection-cell)) {
    width: 32%;
  }

  .social-fund-requests__table th.social-fund-requests__indicator-cell {
    width: 84px;
  }

  .social-fund-requests__table th.social-fund-requests__actions-cell {
    width: 32px;
  }

  .social-fund-requests__table th button,
  .social-fund-requests__table td {
    font-size: 12px;
  }

  .social-fund-requests__table td {
    overflow-wrap: anywhere;
  }
}

@media (max-width: 767px) {
  .social-fund-requests__section-title h2 {
    font-size: 2em;
  }

  .social-fund-requests__search-icon {
    height: 34px;
    width: 34px;
  }

  .social-fund-requests__filters-content {
    padding: 18px 16px 12px;
  }

  .social-fund-requests__filter-actions {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    padding: 0 12px 6px;
  }

  .social-fund-requests__filter-actions .btn {
    flex-basis: auto;
    width: 100%;
  }

  .social-fund-requests__list-heading {
    padding: 16px 16px 10px;
  }

  .social-fund-requests__tabs {
    gap: 0;
    margin: 0 16px 12px;
    overflow-x: auto;
  }

  .social-fund-requests__tabs button {
    flex: 0 0 auto;
    font-size: 12px;
    padding: 10px 8px;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: grid;
    gap: 12px;
    margin: 0 16px;
  }

  .social-fund-requests__mobile-card {
    border-radius: 16px;
    cursor: pointer;
  }

  .social-fund-requests__mobile-card :deep(.el-card__body) {
    padding: 18px 20px !important;
  }

  .social-fund-requests__mobile-card-head {
    align-items: flex-start;
    display: flex;
    gap: 10px;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .social-fund-requests__mobile-card-head>strong {
    color: $bg-blue;
    font-size: 17px;
  }

  .social-fund-requests__mobile-card p {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 128px), 1fr));
    margin: 9px 0 0;
    word-break: break-word;
  }

  .social-fund-requests__mobile-card p b {
    color: $bg-blue;
  }

  .social-fund-requests__pagination-wrap {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .social-fund-requests__pagination {
    align-self: center;
    max-width: 100%;
    overflow: hidden;
  }
}
@media (min-width: 992px) and (max-width: 1399px) {
  .social-fund-requests__table th,
  .social-fund-requests__table td {
    &:nth-child(7 of :not(.social-fund-requests__selection-cell)) {
      display: none;
    }
  }
}

@media (min-width: 992px) and (max-width: 1199px) {
  .social-fund-requests .social-fund-requests__table--agreement {
    th:nth-child(3 of :not(.social-fund-requests__selection-cell)),
    td:nth-child(3 of :not(.social-fund-requests__selection-cell)) { display: none; }
    th:nth-child(1 of :not(.social-fund-requests__selection-cell)) { width: 18%; }
    th:nth-child(2 of :not(.social-fund-requests__selection-cell)) { width: 27%; }
    th:nth-child(4 of :not(.social-fund-requests__selection-cell)) { width: 17%; }
    th:nth-child(5 of :not(.social-fund-requests__selection-cell)) { width: 20%; }
  }
}
</style>
